import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MicIcon } from "../../shared/components/icons";
import { CheckIcon } from "../../shared/components/icons";
import { pickRecorderMime } from "./useCameraStream";
import { captureFileName } from "./captureFileName";
import { useT } from "../../shared/i18n";

interface CapturedMemo {
  file: File;
  url: string;
  durationMs: number;
}

/**
 * Inline live-capture panel (ticket 13), memo only since ux-polish 03: the
 * camera dialog is the single photo-capture surface. Recording starts on the
 * first press of the record control (drive-session hard requirement), then
 * stop → replay → keep. Captured media flows into the form's file list via
 * onCapture.
 */
export function CapturePanel({
  onCapture,
  onClose,
  usedNames = [],
}: {
  onCapture: (file: File, kind: "audio") => void;
  onClose: () => void;
  usedNames?: readonly string[];
}) {
  const t = useT();
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const startedAtRef = useRef<Date | null>(null);
  const autoStartedRef = useRef(false);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [micError, setMicError] = useState(false);
  const [memo, setMemo] = useState<CapturedMemo | null>(null);

  const pauseSupported =
    typeof MediaRecorder !== "undefined" && typeof MediaRecorder.prototype.pause === "function";

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  /**
   * The interval owns the elapsed clock through a ref so the stopped memo gets
   * the true duration even though `onstop` closes over the render that started
   * it; state only mirrors the ref for display.
   */
  const startTimer = () => {
    timerRef.current = setInterval(() => {
      elapsedRef.current += 100;
      setElapsedMs(elapsedRef.current);
    }, 100);
  };

  useEffect(() => clearTimer, []);

  /**
   * The Record CTA opens this panel already recording — one tap total
   * (drive-session hard requirement, ticket 13). The guard keeps the
   * auto-start from re-triggering on re-render.
   */
  useEffect(() => {
    if (!autoStartedRef.current) {
      autoStartedRef.current = true;
      start();
    }
  }, []);

  /** First press starts recording immediately (drive-session hard requirement). */
  const start = () => {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setMicError(true);
      return;
    }
    mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const mime = pickRecorderMime(["audio/webm;codecs=opus", "audio/webm", "audio/ogg"]);
        const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        recorderRef.current = recorder;
        chunksRef.current = [];
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };
        recorder.onstop = () => {
          stream.getTracks().forEach((track) => track.stop());
          clearTimer();
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
          const name = captureFileName("memo", startedAtRef.current ?? new Date(), usedNames);
          const file = new File([blob], name, { type: recorder.mimeType || "audio/webm" });
          setRecording(false);
          setPaused(false);
          setMemo({ file, url: URL.createObjectURL(file), durationMs: elapsedRef.current });
        };
        startedAtRef.current = new Date();
        recorder.start();
        elapsedRef.current = 0;
        setElapsedMs(0);
        setPaused(false);
        setRecording(true);
        startTimer();
      })
      .catch(() => setMicError(true));
  };

  const stop = () => {
    recorderRef.current?.stop();
  };

  const pause = () => {
    const recorder = recorderRef.current;
    if (recorder?.state !== "recording") return;
    recorder.pause();
    clearTimer();
    setPaused(true);
  };

  const resume = () => {
    const recorder = recorderRef.current;
    if (recorder?.state !== "paused") return;
    recorder.resume();
    startTimer();
    setPaused(false);
  };

  const keepMemo = () => {
    if (!memo) return;
    URL.revokeObjectURL(memo.url);
    onCapture(memo.file, "audio");
    onClose();
  };

  const minutes = Math.floor(elapsedMs / 60000);
  const seconds = Math.floor((elapsedMs % 60000) / 1000);
  const durationLabel = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

  return (
    <Box sx={{ mt: 1 }}>
      {memo ? (
        <>
          <Box component="audio" controls preload="metadata" src={memo.url} sx={{ width: "100%" }} />
          <Typography variant="caption" color="text.secondary">
            {durationLabel}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button onClick={() => { URL.revokeObjectURL(memo.url); setMemo(null); }}>
              {t("captureRetake")}
            </Button>
            <Button variant="contained" startIcon={<CheckIcon />} onClick={keepMemo}>
              {t("captureKeep")}
            </Button>
          </Stack>
        </>
      ) : micError ? (
        <Typography variant="body2" color="text.secondary">
          {t("captureMicError")}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={onClose}>{t("cancel")}</Button>
          {recording ? (
            <>
              {pauseSupported &&
                (paused ? (
                  <Button variant="contained" onClick={resume}>
                    {t("captureResume")}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={pause}>
                    {t("capturePause")}
                  </Button>
                ))}
              <Button variant="contained" color="error" onClick={stop}>
                {t("captureStop")}
              </Button>
            </>
          ) : (
            <Button variant="contained" startIcon={<MicIcon />} onClick={start}>
              {t("captureRecord")}
            </Button>
          )}
          <Typography variant="body2">{durationLabel}</Typography>
        </Stack>
      )}
    </Box>
  );
}